import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CopyIcon, SendIcon, GiftIcon } from 'lucide-react';
interface CongratulationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CongratulationsModal({
  isOpen,
  onClose
}: CongratulationsModalProps) {
  const [step, setStep] = useState<'idle' | 'spinning' | 'result'>('idle');
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 52,
    seconds: 5
  });
  const [copied, setCopied] = useState(false);
  const couponCode = 'WOF6WXMBB0X';

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('idle');
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || step !== 'result') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen, step]);
  const handleCopy = () => {
    navigator.clipboard.writeText(couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleSpin = () => {
    setStep('spinning');
    // Simulate spinning for 3 seconds
    setTimeout(() => {
      setStep('result');
    }, 3000);
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-3610" data-mp="jsszay3qkw6v">
      {isOpen && <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" data-id="element-3611" data-mp="xma8boysqqin">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-3612" data-mp="tlqbkmi4xox1" />

          {/* Modal Content */}
          <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-sm flex flex-col items-center z-10" data-id="element-3613" data-mp="88pk6g4wria">
            {/* Close Button */}
            <button onClick={onClose} className="absolute -top-12 right-0 p-2 text-white/80 hover:text-white transition-colors" data-id="element-3614" data-mp="iygpygsg93us">
              <XIcon className="w-8 h-8" data-id="element-3615" data-mp="nze5smpzllt4" />
            </button>

            {step === 'idle' || step === 'spinning' ? <div className="flex flex-col items-center w-full" data-id="element-3616" data-mp="wxzpil7736vh">
                <div className="text-center mb-8" data-id="element-3617" data-mp="wx6lvt3j8xhk">
                  <h2 className="text-3xl font-bold text-white mb-2" data-id="element-3618" data-mp="2tqrrv46fuizx">Spin & Win!</h2>
                  <p className="text-white/80" data-id="element-3619" data-mp="2yp2x11lsg5bt">Try your luck to win exclusive discounts</p>
                </div>

                {/* Wheel */}
                <div className="relative w-64 h-64 mb-10" data-id="element-3620" data-mp="2r6liggz220bz">
                  {/* Pointer */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-white rotate-45 transform origin-bottom z-20 shadow-lg" data-id="element-3621" data-mp="2mebomputkg4f"></div>
                  
                  {/* The Wheel */}
                  <motion.div animate={step === 'spinning' ? {
              rotate: 360 * 5 + 45
            } : {
              rotate: 0
            }} transition={step === 'spinning' ? {
              duration: 3,
              ease: "circOut"
            } : {
              duration: 0
            }} className="w-full h-full rounded-full border-4 border-white shadow-[0_0_30px_rgba(255,255,255,0.3)] overflow-hidden relative" style={{
              background: 'conic-gradient(from 0deg, #f87171 0deg 60deg, #fbbf24 60deg 120deg, #34d399 120deg 180deg, #38bdf8 180deg 240deg, #a78bfa 240deg 300deg, #f472b6 300deg 360deg)'
            }} data-id="element-3622" data-mp="2ci8p7rrcqvl6">
                    {/* Inner Circle */}
                    <div className="absolute inset-0 m-auto w-12 h-12 bg-white rounded-full shadow-inner z-10 flex items-center justify-center" data-id="element-3623" data-mp="38lecwtnvhx4o">
                      <div className="w-8 h-8 bg-gray-100 rounded-full" data-id="element-3624" data-mp="3dez5g66p5ywh"></div>
                    </div>

                    {/* Wheel Sections Text (Simulated) */}
                    <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg drop-shadow-md" data-id="element-3625" data-mp="33o4e4nelg946">
                      <span className="absolute top-8" data-id="element-3626" data-mp="3rb499l7aaqyp">10%</span>
                      <span className="absolute bottom-8" data-id="element-3627" data-mp="5b3ojh440e8">50%</span>
                      <span className="absolute left-8 -rotate-90" data-id="element-3628" data-mp="3icsa2uscqkun">Free</span>
                      <span className="absolute right-8 rotate-90" data-id="element-3629" data-mp="3mdu9pcq6kyx5">20%</span>
                    </div>
                  </motion.div>
                </div>

                <button onClick={handleSpin} disabled={step === 'spinning'} className={`w-full font-bold py-4 rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg text-lg ${step === 'spinning' ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:opacity-90 active:scale-95'}`} data-id="element-3630" data-mp="26vir09d4xutv">
                  {step === 'spinning' ? 'Spinning...' : 'SPIN NOW'}
                </button>
              </div> : <>
                {/* Header */}
                <div className="text-center mb-6" data-id="element-3631" data-mp="1koy62e8g6c4p">
                  <h2 className="text-3xl font-bold text-white flex items-center justify-center gap-2 mb-2" data-id="element-3632" data-mp="1fqd52cldfof7">
                    <span className="text-yellow-400" data-id="element-3633" data-mp="1ufje0sa0gt0a">✨</span> Congratulations
                  </h2>
                  <p className="text-white/80 font-bold tracking-widest uppercase text-sm" data-id="element-3634" data-mp="1pmq8w3amrino">
                    YOU WON
                  </p>
                </div>

                {/* Graphic */}
                <div className="relative w-48 h-48 mb-8" data-id="element-3635" data-mp="wzvo159wih9o">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-purple-600 rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(236,72,153,0.5)]" data-id="element-3636" data-mp="wx9h97uu45si">
                    <div className="w-36 h-36 border-4 border-dashed border-white/30 rounded-full flex items-center justify-center" data-id="element-3637" data-mp="1qmqezptip54g">
                      <span className="text-6xl font-black text-white" data-id="element-3638" data-mp="20f5ywsodk3rz">%</span>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-32 h-12 bg-[#4A148C] rounded-xl transform -rotate-12 flex flex-col justify-center px-4 shadow-lg border border-purple-400/30" data-id="element-3639" data-mp="13pf22msm24j0">
                    <div className="w-12 h-1.5 bg-white/80 rounded-full mb-1.5" data-id="element-3640" data-mp="1co0k8gu67hj9"></div>
                    <div className="w-8 h-1.5 bg-white/80 rounded-full" data-id="element-3641" data-mp="1hooc14q842ej"></div>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full" data-id="element-3642" data-mp="2o47c0t85k33l"></div>
                  </div>
                </div>

                {/* Coupon Section */}
                <div className="w-full text-center mb-6" data-id="element-3643" data-mp="hfyjz5jwg16g">
                  <p className="text-white/80 font-bold tracking-widest uppercase text-xs mb-3" data-id="element-3644" data-mp="hff5jytgjux6">
                    YOUR COUPON CODE
                  </p>
                  <div className="border-2 border-dashed border-white/40 rounded-2xl p-4 flex items-center justify-center gap-4 bg-white/5 backdrop-blur-md mb-4" data-id="element-3645" data-mp="7pn4qmurm5k9">
                    <span className="text-2xl font-black text-white tracking-wider" data-id="element-3646" data-mp="2ozei3ed1sr3">
                      {couponCode}
                    </span>
                    <button onClick={handleCopy} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-pink-500 hover:bg-pink-50 transition-colors" data-id="element-3647" data-mp="3pvi9bthg8zg8">
                      {copied ? <span className="text-xs font-bold text-green-500" data-id="element-3648" data-mp="1j520jnb8mrds">Copied</span> : <CopyIcon className="w-5 h-5" data-id="element-3649" data-mp="1e5z7l18j8bm6" />}
                    </button>
                  </div>

                  {/* Timer */}
                  <div className="inline-flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full px-4 py-2 text-white text-sm font-medium" data-id="element-3650" data-mp="n37nkd3ane18">
                    VALID UNTIL {String(timeLeft.hours).padStart(2, '0')}:
                    {String(timeLeft.minutes).padStart(2, '0')}:
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </div>
                </div>

                {/* Actions */}
                <div className="w-full space-y-4" data-id="element-3651" data-mp="99qddiwhzdnv">
                  <button className="w-full bg-black text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-900 transition-colors shadow-lg" data-id="element-3652" data-mp="973w2rjt1jsg">
                    <SendIcon className="w-5 h-5" data-id="element-3653" data-mp="3vgyfmcc4f4fo" />
                    Share to Unlock Extra Spin
                  </button>
                  
                  <button onClick={onClose} className="w-full text-white font-bold py-2 hover:text-gray-200 transition-colors" data-id="element-3654" data-mp="1tp1s04coi44j">
                    Continue Shopping
                  </button>

                  <div className="flex items-center justify-center gap-2 text-white/80 text-sm" data-id="element-3655" data-mp="3a7lpu8rf2693">
                    <GiftIcon className="w-4 h-4" data-id="element-3656" data-mp="3nyqkewfxg8ck" />
                    <span data-id="element-3657" data-mp="3j5xdq30wepxu">Extra spin available after sharing</span>
                  </div>
                </div>
              </>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}